import { isApolloError, gql } from '@apollo/client';
import { ComponentSpec } from '@digibee/flow';
import axios from 'axios';

import clientApollo from './clientApollo';

import { API_KEY, URL } from '~/common/helpers/environment';
import { Pipeline } from '~/entities/Pipeline';
import { ConnectorOpenAPISpec } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ImportSpec/types';
import { TestGroup } from '~/scenes/Build/scenes/FlowBuilder/components/Design/machines/TestCase.machine';

type GenerateAIDocumentationParams = {
  realm: string;
  triggerSpec: Record<string, unknown>;
  flowSpec: Record<string, unknown>;
  name: string;
  versionMajor: number;
  versionMinor: number;
};

export type GenerateAIDocumentationData = {
  description: string;
  systemsInvolved: ComponentSpec[];
  events: ComponentSpec[];
  // eslint-disable-next-line camelcase
  globals: (ComponentSpec & { global_variables: string[] })[];
  // eslint-disable-next-line camelcase
  accounts: (ComponentSpec & { global_variables: string[] })[];
  uniqueEvents: string[];
  uniqueGlobals: string[];
  uniqueAccounts: string[];
};

type GenerateAIDocumentationResult = {
  generateAIDocumentation: GenerateAIDocumentationData;
};

type PipelineFetchData = {
  id: string;
  realm: string;
};

type SensitiveFieldsProp = {
  logSensitiveFields: string[];
};

type PipelineFetchResult = {
  pipelineV2: Pick<
    Pipeline,
    | 'id'
    | 'name'
    | 'description'
    | 'draft'
    | 'versionMajor'
    | 'versionMinor'
    | 'inSpec'
    | 'outSpec'
    | 'triggerSpec'
    | 'hasAlert'
    | 'alert'
    | 'thumbnailName'
    | 'flowSpec'
    | 'disconnectedFlowSpecs'
    | 'canvasVersion'
    | 'disabled'
    | 'parameterizedReplica'
  >;
  sensitiveFields: SensitiveFieldsProp | null;
};

type SavePipelineImageResult = {
  contentName: string;
  success: boolean;
  url: string;
};

type SavePipelineImageData = {
  token: string;
  image: string;
  realm: string;
};

type SavePipelineData = {
  realm: string;
  trackingId: string;
  pipeline: Pipeline;
  projectId: string;
  folderId?: string;
  mocks: { mockId?: string }[];
  testCases: TestGroup[];
};

type SavePipeline = Pick<
  Pipeline,
  | 'id'
  | 'name'
  | 'description'
  | 'draft'
  | 'parameterizedReplica'
  | 'thumbnailName'
  | 'versionMajor'
  | 'versionMinor'
>;

type SavePipelineV2Result = {
  updatePipelineV2: SavePipeline;
};

type UpdateSensitiveFieldsData = {
  realm: string;
  pipelineId: string;
  sensitiveFields: string[];
};
type GenerateOpenApiSpecData = {
  token: string;
  file: File | null;
};

type ParseOpenApiForConnectorData = {
  token: string;
  file: File;
};

type UpdateSentiveFieldsResult = {
  updatePipelineSensitiveFields: boolean;
};

type RevertPipelineCanvasVersionData = {
  realm: string;
  pipelineId: string;
  trackingId: string;
};

type CheckUserPipelineAccessData = {
  realm: string;
  pipelineId: string;
};

type RevertPipelineResult = {
  revertPipelineCanvasVersion: SavePipeline;
};

export interface ApiMethodResponses {
  statusCode: string;
  description: string;
  example: any;
  id?: string;
}

interface ApiMethod {
  id: string;
  method: string;
  summary: string;
  responses?: ApiMethodResponses[];
}

export interface ApiRoutes {
  path: string;
  methods: ApiMethod[];
}

const pipelineV2 = {
  getPipeline: async (variables: PipelineFetchData) => {
    try {
      const result = await clientApollo.query<PipelineFetchResult>({
        query: gql`
          query data($realm: String!, $id: ID!) {
            pipelineV2(realm: $realm, id: $id) {
              testCases
              id
              name
              description
              draft
              versionMajor
              versionMinor
              inSpec
              outSpec
              parameterizedReplica
              triggerSpec
              triggerCategory
              hasAlert
              alert
              thumbnailName
              flowSpec
              disconnectedFlowSpecs
              mocks
              stepsDocumentations
              disabled
              canvasVersion
              componentsCount
              connectedOnFlowComponentsCount
              usedComponents
              suggestedComponentsIds
              createdAt
              lastModified
              restApiTriggerRoutes
              isUsingApiTrigger
              mcpServerTriggerTools
              isUsingMcpServerTrigger
              projectId
            }
            sensitiveFields(realm: $realm, id: $id) {
              logSensitiveFields
            }
          }
        `,
        variables
      });

      return {
        data: {
          pipeline: result.data.pipelineV2,
          sensitiveFields: result.data.sensitiveFields
        }
      };
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },
  saveImage: async ({
    token,
    image,
    realm
  }: SavePipelineImageData): Promise<string> => {
    try {
      const formData = new FormData();
      const name = `${crypto.randomUUID()}.jpg`;
      const file = new File([image], name, { type: 'image/jpg' });
      const headers = {
        'Content-Type': 'multipart/form-data',
        apikey: API_KEY,
        authorization: token
      };

      formData.append('file', file);

      const { data } = await axios.post<SavePipelineImageResult>(
        `${URL}/design/realms/${realm}/staticContent`,
        formData,
        { headers }
      );

      return data.contentName;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new Error(e.message);
      }
      throw e;
    }
  },
  save: async ({
    realm,
    trackingId,
    pipeline,
    projectId,
    folderId,
    mocks = [],
    testCases = []
  }: SavePipelineData) => {
    try {
      const response = await clientApollo.mutate<SavePipelineV2Result>({
        mutation: gql`
          mutation updatePipelineV2(
            $realm: String!
            $trackingId: String!
            $pipeline: PipelineVersionInput!
            $projectId: String
            $folderId: String
            $mocks: JSON
            $testCases: JSON
          ) {
            updatePipelineV2(
              realm: $realm
              pipeline: $pipeline
              trackingId: $trackingId
              projectId: $projectId
              folderId: $folderId
              mocks: $mocks
              testCases: $testCases
            ) {
              id
              name
              description
              draft
              versionMajor
              versionMinor
              parameterizedReplica
              thumbnailName
            }
          }
        `,
        variables: {
          realm,
          trackingId,
          pipeline,
          projectId,
          folderId,
          mocks,
          testCases
        }
      });

      return response.data?.updatePipelineV2 || {};
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },

  async updateSensitiveFields({
    realm,
    pipelineId,
    sensitiveFields
  }: UpdateSensitiveFieldsData): Promise<boolean> {
    try {
      const response = await clientApollo.mutate<UpdateSentiveFieldsResult>({
        mutation: gql`
          mutation updatePipelineSensitiveFieldsV2(
            $realm: String!
            $pipelineId: String!
            $sensitiveFields: [String]
          ) {
            updatePipelineSensitiveFields(
              realm: $realm
              pipelineId: $pipelineId
              sensitiveFields: $sensitiveFields
            )
          }
        `,
        variables: {
          realm,
          pipelineId,
          sensitiveFields
        }
      });

      return response.data?.updatePipelineSensitiveFields || false;
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },
  async generateAIDocumentation({
    realm,
    name,
    versionMajor,
    versionMinor,
    triggerSpec,
    flowSpec
  }: GenerateAIDocumentationParams): Promise<GenerateAIDocumentationData> {
    try {
      const response = await clientApollo.mutate<GenerateAIDocumentationResult>(
        {
          mutation: gql`
            mutation generateAIDocumentation(
              $realm: String!
              $lang: String!
              $pipeline: PipelineAIDocumentationInput!
            ) {
              generateAIDocumentation(
                realm: $realm
                lang: $lang
                pipeline: $pipeline
              )
            }
          `,
          variables: {
            realm,
            lang: navigator.language || 'en-US',
            pipeline: {
              triggerSpec,
              flowSpec,
              name,
              versionMajor,
              versionMinor
            }
          }
        }
      );

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      return response.data?.generateAIDocumentation!;
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },
  generateOpenApiSpec: async ({
    token,
    file
  }: GenerateOpenApiSpecData): Promise<{ paths: ApiRoutes[] }> => {
    try {
      try {
        const formData = new FormData();
        const headers = {
          'Content-Type': 'multipart/form-data',
          apikey: API_KEY,
          authorization: token
        };

        formData.append('file', file as File);

        const { data } = await axios.post(
          `${URL}/design/open-api/parse`,
          formData,
          { headers }
        );

        return data.openAPISpec;
      } catch (e) {
        if (axios.isAxiosError(e)) {
          throw e.response?.data;
        }
        throw e;
      }
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },
  parseOpenApiForConnector: async ({
    token,
    file
  }: ParseOpenApiForConnectorData): Promise<ConnectorOpenAPISpec> => {
    try {
      const formData = new FormData();
      const headers = {
        'Content-Type': 'multipart/form-data',
        apikey: API_KEY,
        authorization: token
      };

      formData.append('file', file);

      const { data } = await axios.post(
        `${URL}/design/open-api/parse-connector`,
        formData,
        { headers }
      );

      return data;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw e.response?.data;
      }
      throw e;
    }
  },
  revertPipelineCanvasVersion: async ({
    realm,
    pipelineId,
    trackingId
  }: RevertPipelineCanvasVersionData) => {
    try {
      const response = await clientApollo.mutate<RevertPipelineResult>({
        mutation: gql`
          mutation revertPipelineCanvasVersion(
            $realm: String!
            $pipelineId: String!
            $trackingId: String!
          ) {
            revertPipelineCanvasVersion(
              realm: $realm
              pipelineId: $pipelineId
              trackingId: $trackingId
            ) {
              id
              name
              description
              draft
              versionMajor
              versionMinor
              parameterizedReplica
              thumbnailName
            }
          }
        `,
        variables: {
          realm,
          pipelineId,
          trackingId
        }
      });

      return response.data?.revertPipelineCanvasVersion || {};
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  },
  checkUserPipelineAccess: async ({
    realm,
    pipelineId
  }: CheckUserPipelineAccessData) => {
    try {
      const response = await clientApollo.query({
        query: gql`
          query checkUserPipelineAccess($realm: String!, $pipelineId: String!) {
            checkUserPipelineAccess(realm: $realm, pipelineId: $pipelineId)
          }
        `,
        variables: {
          realm,
          pipelineId
        }
      });

      return response.data?.checkUserPipelineAccess;
    } catch (e) {
      if (e instanceof Error && isApolloError(e)) throw Error(e.message);
      throw e;
    }
  }
};

export default pipelineV2;
